import { useHead } from '@unhead/react';
import { Button, Descriptions } from 'antd';
import { useTranslation } from 'translations/hooks';
import { Content } from 'ui/components/content';
import type { ScanConfigV1OutputDto } from 'generated/apiSchemas';
import { getLocalStorageValue } from 'technical/local-storage';
import {
  scanPatternSchema,
  segmentTypesSchema,
  type ScanPatternSchema,
  type SegmentTypesSchema,
} from 'technical/local-storage/types';
import {
  SCAN_PATTERN_KEY,
  SEGMENT_TYPES_KEY,
} from 'technical/local-storage/constants';
import { useState } from 'react';
import { useSyncSettings } from 'business/settings/hooks/use-sync-settings';
import { MainCard } from 'ui/components/main-card';

export const SettingsSegmentConfigPage = () => {
  const { t } = useTranslation();
  const { synchronize, isSyncing } = useSyncSettings();

  useHead({ title: t('settings.segmentConfig.title') });

  const [localValues, setLocalValues] = useState({
    scanPattern: getLocalStorageValue<ScanPatternSchema>({
      key: SCAN_PATTERN_KEY,
      schema: scanPatternSchema,
    }),
    segmentTypes: getLocalStorageValue<SegmentTypesSchema>({
      key: SEGMENT_TYPES_KEY,
      schema: segmentTypesSchema,
    }),
  });

  const generatePreview = (scanConfig: ScanConfigV1OutputDto | null) => {
    if (!scanConfig) {
      return '';
    }

    let preview = '';
    for (let i = 0; i < 15; i++) {
      if (
        i >= scanConfig.startIndex &&
        i < scanConfig.startIndex + scanConfig.extractLength
      ) {
        preview += 'X ';
      } else {
        preview += '. ';
      }
    }
    preview += '_';
    return preview;
  };

  const items = [
    {
      key: 'scanConfig.index',
      label: t('settings.segmentConfig.startIndex'),
      children: localValues.scanPattern?.startIndex,
    },
    {
      key: 'scanConfig.length',
      label: t('settings.segmentConfig.extractLength'),
      children: localValues.scanPattern?.extractLength,
    },
    {
      key: 'scanConfig.preview',
      label: t('settings.segmentConfig.preview'),
      children: generatePreview(localValues.scanPattern),
    },
    {
      key: 'segmentDrawConfig',
      label: t('settings.segmentConfig.segmentTypes'),
      children: localValues.segmentTypes
        ? localValues.segmentTypes.join(' - ')
        : '-',
    },
  ];

  const handleSynchronize = async () => {
    const result = await synchronize();

    if (result.success) {
      setLocalValues({
        scanPattern: getLocalStorageValue({
          key: SCAN_PATTERN_KEY,
          schema: scanPatternSchema,
        }),
        segmentTypes: getLocalStorageValue({
          key: SEGMENT_TYPES_KEY,
          schema: segmentTypesSchema,
        }),
      });
    }
  };

  return (
    <Content className="flex w-full flex-1 flex-col items-center justify-between py-4">
      <MainCard>
        <Descriptions items={items} />
      </MainCard>

      <div className="w-full">
        <Button
          type="primary"
          className="w-full"
          onClick={handleSynchronize}
          loading={isSyncing}
        >
          {t('common.synchronize')}
        </Button>
      </div>
    </Content>
  );
};
